import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Languages } from "lucide-react";
import { useLang, useT } from "@/context/LanguageContext";

export default function Header({ contact, logoUrl }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { lang, toggle } = useLang();
  const { ui } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { label: ui("nav.services"), href: "#servicios" },
    { label: ui("nav.manifesto"), href: "#manifiesto" },
    { label: ui("nav.pricing"), href: "#precios" },
    { label: ui("nav.gallery"), href: "#galeria" },
    { label: ui("nav.contact"), href: "#contacto" },
  ];

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`mx-auto transition-all duration-500 ${
          scrolled ? "mt-3 max-w-6xl bg-[#f5f1e8]/85 backdrop-blur-md border border-[color:var(--gcs-line)]" : "mt-0 max-w-none bg-transparent border border-transparent"
        } rounded-full`}
      >
        <div className="flex items-center justify-between px-5 md:px-8 py-3">
          <a href="#top" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={logoUrl}
              alt="General Cleaning Services logo"
              className="h-10 w-10 md:h-11 md:w-11 object-contain rounded-sm"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[15px] font-medium tracking-tight text-[color:var(--gcs-emerald)]">
                General Cleaning
              </span>
              <span className="overline text-[9px] text-[color:var(--gcs-gold)]">Services, LLC</span>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className="text-sm font-medium text-[color:var(--gcs-emerald)] gcs-underline"
                data-testid={`nav-link-${n.href.replace('#', '')}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              onClick={toggle}
              aria-label={ui("lang.switch")}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[color:var(--gcs-emerald)]/40 text-[color:var(--gcs-emerald)] px-3 py-2 text-[11px] font-semibold hover:bg-[color:var(--gcs-emerald)] hover:text-[color:var(--gcs-ivory)] transition-colors"
              data-testid="header-lang-toggle"
            >
              <Languages className="w-3.5 h-3.5" />
              <span className={lang === "en" ? "text-[color:var(--gcs-gold)]" : ""}>EN</span>
              <span className="opacity-50">/</span>
              <span className={lang === "es" ? "text-[color:var(--gcs-gold)]" : ""}>ES</span>
            </button>

            <a
              href={`tel:${contact?.phone?.replace(/[^0-9+]/g, "") || ""}`}
              className="hidden md:inline-flex items-center gap-2 rounded-full btn-outline-emerald px-4 py-2 text-xs font-semibold"
              data-testid="header-call-btn"
            >
              <Phone className="w-3.5 h-3.5" />
              {contact?.phone}
            </a>
            <a
              href="#contacto"
              className="hidden md:inline-flex items-center rounded-full btn-emerald px-4 py-2 text-xs font-semibold"
              data-testid="header-cta"
            >
              {ui("cta.quote")}
            </a>
            <button
              className="md:hidden p-2 text-[color:var(--gcs-emerald)]"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="header-mobile-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden absolute top-full inset-x-3 mt-2 bg-[#f5f1e8] border border-[color:var(--gcs-line)] rounded-2xl p-6"
          >
            <div className="flex flex-col gap-4">
              {nav.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-lg font-serif text-[color:var(--gcs-emerald)]"
                  data-testid={`mobile-nav-${n.href.replace('#', '')}`}
                >
                  {n.label}
                </a>
              ))}
              <button
                onClick={() => { toggle(); setOpen(false); }}
                className="inline-flex items-center gap-2 text-sm text-[color:var(--gcs-gold)] gcs-underline"
                data-testid="mobile-lang-toggle"
              >
                <Languages className="w-4 h-4" />
                {lang === "en" ? "Cambiar a español" : "Switch to English"}
              </button>
              <a
                href="#contacto"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center rounded-full btn-emerald px-4 py-3 text-sm font-semibold"
                data-testid="mobile-cta"
              >
                {ui("cta.quote_request")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
